import React, {useState, useEffect} from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Radio,
	RadioGroup,
	FormControlLabel,
	Box,
	CircularProgress,
	IconButton,
	Divider,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import API from '../../../../network/dashboardAPI';
import EditCustomerAddress from '../../../../components/EditCustomerAddress/EditCustomerAddress';

const useStyles = makeStyles((theme) => ({
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingBottom: 0,
	},
	addressCard: {
		border: '1px solid #e0e0e0',
		borderRadius: 8,
		padding: 12,
		marginBottom: 12,
		cursor: 'pointer',
		transition: 'all 0.2s',
		'&:hover': {
			borderColor: theme.palette.primary.main,
			backgroundColor: '#f5f5f5',
		},
	},
	selectedCard: {
		borderColor: theme.palette.primary.main,
		borderWidth: 2,
		backgroundColor: '#e3f2fd',
	},
	addressLabel: {
		fontWeight: 'bold',
		color: theme.palette.primary.main,
		marginBottom: 4,
	},
	addressText: {
		color: '#666',
		fontSize: '0.9rem',
	},
	currentBadge: {
		backgroundColor: '#4caf50',
		color: 'white',
		padding: '2px 8px',
		borderRadius: 4,
		fontSize: '0.75rem',
		marginLeft: 8,
	},
	addButton: {
		borderStyle: 'dashed',
		marginTop: 8,
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: 200,
	},
	noAddressText: {
		textAlign: 'center',
		color: '#999',
		padding: 40,
	},
}));

const ChangeAddressDialog = ({
	                             open,
	                             onClose,
	                             customerId,
	                             currentAddressId,
	                             orderId,
	                             onAddressChanged,
	                             customerData,
                             }) => {
	const classes = useStyles();
	const [addresses, setAddresses] = useState([]);
	const [selectedAddressId, setSelectedAddressId] = useState(currentAddressId);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [showEditDialog, setShowEditDialog] = useState(false);
	const [editingAddress, setEditingAddress] = useState(null);
	const [isNewAddress, setIsNewAddress] = useState(false);

	useEffect(() => {
		if (open && customerId) {
			fetchAddresses();
		}
	}, [open, customerId]);

	useEffect(() => {
		setSelectedAddressId(currentAddressId);
	}, [currentAddressId]);

	const fetchAddresses = async () => {
		setLoading(true);
		try {
			const response = await API.getCustomerAddresses(customerId);
			const data = await response.json();
			if (data.success) {
				setAddresses(data.data || []);
			}
		} catch (error) {
			console.error('Failed to fetch addresses:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSave = () => {
		if (selectedAddressId === currentAddressId) {
			onClose();
			return;
		}

		// Find the selected address object
		const selectedAddress = addresses.find(addr => addr.address_id === selectedAddressId);

		// Return selected address to parent (not saving to DB yet)
		onAddressChanged && onAddressChanged(selectedAddress);
		onClose();
	};

	const handleAddNewAddress = () => {
		setIsNewAddress(true);
		setEditingAddress({});
		setShowEditDialog(true);
	};

	const handleEditAddress = (address) => {
		setIsNewAddress(false);
		setEditingAddress(address);
		setShowEditDialog(true);
	};

	const handleEditDialogClose = () => {
		setShowEditDialog(false);
		setEditingAddress(null);
		setIsNewAddress(false);
	};

	const handleAddressUpdated = (updatedAddresses) => {
		setAddresses(updatedAddresses);
	};

	const formatAddress = (addr) => {
		const parts = [
			addr.house_and_street || `${addr.house_number || ''} ${addr.street_details || ''}`.trim(),
			addr.apartment_name,
			addr.commune,
			addr.district,
			addr.city,
			addr.area_details,
		].filter(Boolean);
		return parts.join(', ');
	};

	return (
		<>
			<Dialog open={open && !showEditDialog} onClose={onClose} fullWidth maxWidth="sm">
				<DialogTitle disableTypography className={classes.dialogTitle}>
					<Typography variant="h6">Change Delivery Address</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon/>
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{loading ? (
						<Box className={classes.loadingContainer}>
							<CircularProgress/>
						</Box>
					) : addresses.length === 0 ? (
						<Typography className={classes.noAddressText}>
							No addresses found for this customer
						</Typography>
					) : (
						<RadioGroup
							value={selectedAddressId}
							onChange={(e) => setSelectedAddressId(parseInt(e.target.value))}
						>
							{addresses.map((addr) => (
								<Box
									key={addr.address_id}
									className={`${classes.addressCard} ${
										selectedAddressId === addr.address_id ? classes.selectedCard : ''
									}`}
									onClick={() => setSelectedAddressId(addr.address_id)}
								>
									<FormControlLabel
										value={addr.address_id}
										control={<Radio color="primary"/>}
										label={
											<Box>
												<Box display="flex" alignItems="center">
													<LocationOnIcon fontSize="small" color="primary"/>
													<Typography className={classes.addressLabel}>
														{addr.nick_name || 'Address'}
													</Typography>
													{addr.address_id === currentAddressId && (
														<span className={classes.currentBadge}>Current</span>
													)}
												</Box>
												<Typography className={classes.addressText}>
													{formatAddress(addr)}
												</Typography>
												{addr.mobile_number && (
													<Typography variant="caption" color="textSecondary">
														Phone: +{addr.country_code || '855'} {addr.mobile_number}
													</Typography>
												)}
												{addr.delivery_instructions && (
													<Typography variant="caption" display="block" color="textSecondary">
														Note: {addr.delivery_instructions}
													</Typography>
												)}
											</Box>
										}
										style={{width: '100%', margin: 0}}
									/>
								</Box>
							))}
						</RadioGroup>
					)}

					<Divider style={{margin: '16px 0'}}/>

					<Button
						variant="outlined"
						fullWidth
						startIcon={<AddIcon/>}
						onClick={handleAddNewAddress}
						className={classes.addButton}
					>
						Add New Address
					</Button>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} color="default">
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						color="primary"
						variant="contained"
						disabled={saving || selectedAddressId === currentAddressId}
					>
						{saving ? 'Saving...' : 'Save'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Edit/Create Address Dialog */}
			{showEditDialog && (
				<EditCustomerAddress
					modal={showEditDialog}
					handleToggle={handleEditDialogClose}
					data={editingAddress}
					userData={customerData}
					addressEdit={!isNewAddress}
					setIsUpdateAddressRequest={() => {
					}}
					setItemListDeliveryAddr={handleAddressUpdated}
					setListAddress={handleAddressUpdated}
				/>
			)}
		</>
	);
};

export default ChangeAddressDialog;
